/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosResponse } from 'axios';
import {
  getSessionData,
  getPreProcessorUrl,
  isServerSession,
  getPreProcessorFromCookie,
  isServerPreprocessor,
  getDynamicPageModel,
  redirectTo,
  getPreProcessorRedirectURLs,
  evaluatePreData,
  targetPayloadGenerator,
  isSSTEnabled,
  getTAGPCookie,
  isBulgariFlowRequest,
  HarnessConfigMap,
  isDttProperty,
  getHeaderValue,
  getSecureFieldFlag,
  getNGSFFlag,
  stringToBoolean,
  getHybridPageTargetExperience,
  IGetHybridPageTargetExperienceArgs,
  getHarnessFeatureFlagClient,
  transformResponse,
  getSocialProofingData,
  getRemoteSpaLocalesForDomain,
  RemoteSpaLocalesConfig,
  createIsFeatureRequested,
  addSubDirectoryPrefix,
  getDttRequestedFeatureHeaders,
  isRemoteSpaEnabled,
} from './helper';
import { constants, URL_CONSTANTS, ACRS_CONSTANTS, FEATURE_FLAG_KEYS, envVarKeys } from '../constants';
import { getDataLayer } from './dataLayer';
import { RequestProps } from './dataLayer/index.types';
import { logger } from './logger';
import { interceptor } from './interceptor';
import { ServerResponse } from 'http';
import { axiosWrapper } from './axiosClient';
import {
  getTargetExpSegment, replaceKeyInJson, getAdobeAEPData,
  createDefaultPayload
} from '@marriott/shared/mi-helper-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { FEATURE_HEADERS } from '../constants/featureHeaders';
import { isACDLEnabled } from './bookDataLayer';
const {
  NEXT_PUBLIC_SESSION_KEYS,
  NEXT_PUBLIC_IS_DTT,
  NEXT_PUBLIC_IS_ACRS,
  NEXT_PUBLIC_IS_NRP,
  NEXT_PUBLIC_ENABLE_HARNESS,
  NEXT_PUBLIC_DTT_PROPERTIES = '',
} = process.env;
const { DATALAYERTYPE } = process.env;
const {
  ENV_DEV,
  SESSION_VALIDATION_FAILED_OR_EXPIRED,
  CART_VALIDATION_FAILED_OR_EXPIRED,
  DEFAULT_LANGUAGE,
  DEFAULT_LOCALE,
  MARRIOTT_BRAND_CODE,
  SERVER_TARGET_MBOX_CLIENT,
  SERVER_TARGET_MBOX_VERSION,
  TARGET_COOKIE_NAME,
  LOOKUP_RESERVATION_FOR_AUTH_FLOW_302_TO_MYTRIPS,
  IS_TAGP_COOKIE,
  SPECIAL_RATE_CONTROL,
  SPECIAL_RATE_VARIANT,
  NEW_RLM_REDESIGN,
  R0_CHECKOUT_GUEST_EXP_SEGMENT,
  R0_CHECKOUT_MEMBER_EXP_SEGMENT,
  SERVER_TARGET_MBOX_SECURE_FIELDS,
  SERVER_TARGET_R0_CHECKOUT_NAME,
  REMEMBERED_STATE,
  TRUE,
  DATA_LAYER_PAGE_EXPERIENCE_CO,
  DTT_GATE_FLAG_KEY,
  DTT_FEATURE_GATE_HEADER_PASS_VALUE,
  DISABLE_MI_FOR_RLM,
} = constants;
const { ACRS_FILTERS, PERSISTENT_CART, CRS_INVENTORY_NAME, ENABLE_NEW_LEGAL_CONSENT, SECURE_FIELDS_ENABLED } =
  FEATURE_HEADERS;
const { NEXT_PUBLIC_PROXY_HOST } = process.env;
const { NEXT_PUBLIC_MAXBRAND_DISABLE_FLAG } = process.env;
const { NEXT_PUBLIC_FALLBACK_PREPROCESSOR_MOCK_ENABLE = '' } = process.env;
const { NEXT_PUBLIC_BOOK_KEYS = '' } = process.env;
const { NEXT_PUBLIC_NEWGUESTINFO_EXP_LOCALES = '' } = process.env;
const {
  APOLLOGRAPHQL_PUBLIC_ACCEPT_LANG = '',
  APOLLOGRAPHQL_PUBLIC_CLIENT_VERSION = '',
  APOLLOGRAPHQL_PUBLIC_CLIENT_NAME = '',
  APOLLOGRAPHQL_PUBLIC_APPLICATION_NAME = '',
  APOLLOGRAPHQL_PUBLIC_REQUIRE_SAFELISTING = '',
  APOLLOGRAPHQL_FULL_NGINX_ENDPOINT = '',
  APOLLOGRAPHQL_PUBLIC_LOCAL_DEV = '',
  APOLLOGRAPHQL_PUBLIC_LOWER_ENV_PATH = '',
  APOLLOGRAPHQL_PUBLIC_HIGHER_ENV_PATH = '',
  DEPLOYED_ENV_TYPE = '',
  APOLLOGRAPHQL_PUBLIC_DEBUG_LOG = '',
  APOLLOGRAPHQL_LOCAL_ENDPOINT = '',
} = process.env;

declare global {
  // eslint-disable-next-line no-var
  var loggerInstance: CallableFunction;
}

const redirect = getPreProcessorRedirectURLs();

interface axiosResponseInterface extends AxiosResponse {
  notFound?: boolean;
  errorMessage?: string;
  experienceSegments?: { brandCode: string; crsSystem: string };
  locale?: string;
  languageTag?: string;
  navigationURL?: string;
}

/**
 *
 * @param url
 * @param header
 * @param body
 * @param isDev
 * @returns API response
 * This function is calling the pre-processir url and get the response, to be used in calling the page model.
 */
export const preProcessorCall = async (
  url: string,
  header: Record<string, string>,
  body: Record<string, string | boolean>,
  isDev: boolean
): Promise<axiosResponseInterface> => {
  const { log, pLog } = global.loggerInstance('preProcessorCall');
  log.debug(`API url: ${url}`);
  const apiStartTime = new Date().getTime();
  if (isDev) {
    //process.env['NODE_TLS_REJECT_UNAUTHORIZED'] = '0'; //To avoid the SSL error on local env
  }
  let response;
  try {
    response = await axiosWrapper.post(url, body, {
      headers: header,
    });
    log.debug(`API call success with status code: ${response.status}`);
  } catch (e: any) {
    log.error(`API call failed: ${e}`);
    const status = e?.response?.data?.status;
    if (
      status === SESSION_VALIDATION_FAILED_OR_EXPIRED ||
      status === CART_VALIDATION_FAILED_OR_EXPIRED ||
      status === LOOKUP_RESERVATION_FOR_AUTH_FLOW_302_TO_MYTRIPS
    ) {
      log.error(status);
      return {
        ...(response?.data || {}),
        errorMessage: status,
      };
    } else {
      if (NEXT_PUBLIC_FALLBACK_PREPROCESSOR_MOCK_ENABLE === 'true') {
        log.error(`Preprocessor API mocked enabled because: ${e}`);
        const locale = header?.['accept-language']?.includes(DEFAULT_LANGUAGE)
          ? DEFAULT_LANGUAGE
          : header?.['accept-language'];
        const data = await preProcessorCallMocked('', locale, locale);
        return data as unknown as axiosResponseInterface;
      }
      throw new Error('PreProcessor call failed');
    }
  }
  if (response?.status !== 200) {
    log.error(`API url: ${url} | response status is: ${response?.status}`);
    return {
      ...(response?.data || {}),
      notFound: true,
      errorMessage: '',
    };
  }
  pLog.log('API call performance timing', apiStartTime, new Date().getTime());
  return response.data as axiosResponseInterface;
};

export const preProcessorCallMocked = async (
  brandCode = MARRIOTT_BRAND_CODE,
  locale = DEFAULT_LOCALE,
  languageTag = DEFAULT_LANGUAGE,
  crsSystem = ACRS_CONSTANTS?.ACRS
) => {
  const { log, pLog } = global.loggerInstance('preProcessorCallMocked');
  const apiStartTime = new Date().getTime();
  let response;
  try {
    response = {
      status: 200,
      data: {
        experienceSegments: {
          hotelBrand: brandCode,
          brandCode: brandCode,
          ersProduct: 'false',
          programCode: null,
          crsSystem: crsSystem,
          ers: 'true',
        },
        locale: locale,
        userState: 'UnAuthenticated',
        notFound: false,
        errorMessage: '',
        languageTag: languageTag,
      },
    };
    log.debug(`API call success with status code: ${response.status}`);
  } catch (e) {
    log.error(`API call failed: ${e}`);
    throw new Error('PreProcessor call failed');
  }
  if (response?.status !== 200) {
    log.error(`response status is: ${response?.status}`);
    return {
      ...(response?.data || {}),
      notFound: true,
      errorMessage: '',
    };
  }
  pLog.log('API call performance timing', apiStartTime, new Date().getTime());
  return response.data;
};

// Initializing Harness Feature Flag instance once here.
// Will be served to preProcessor as a closure.
const { featureFlagService, target } = getHarnessFeatureFlagClient();

/**
 *
 * @param gssp
 * @returns props, modelProps
 * props: props to be passed to the page
 * modelProps: props to be passed to the model
 * This function is used to excute the calls before the page gets rendered.
 * session, interceptor, pre-process, page model, datalayer
 */

export function prePageCall(gssp: (preData: any, model?: any, featureFlag?: any, pageUrl?: string) => any) {
  return async (context: any) => {
    const {
      resolvedUrl,
      req,
      res,
    }: {
      resolvedUrl: string;
      req: {
        cookies?: {
          sessionID: string;
          dtt: boolean;
          UserIdToken: string;
          target_rendering_enabled: string;
          BVSAuthCookie: string;
          mippIDCookie: string;
          MI_SITE: string;
          RememberMeFlag: string;
        };
        headers: RequestProps;
        query: Record<string, unknown>;
        url: string;
        originalUrl: string;
      };
      res: ServerResponse;
    } = context;
    try {
      global.loggerInstance = logger({
        sessionID: req?.cookies?.sessionID,
        requestID: req.headers['x-request-id'],
      }) as CallableFunction;
      const contextUrl = resolvedUrl ?? req.originalUrl ?? req.url;
      const pageUrl = `${contextUrl?.split('?')?.[0]}`;
      const queryParams = `${contextUrl?.split('?')?.[1]}`;
      const requestId = req.headers['x-request-id'];
      const { log, pLog } = global.loggerInstance('Pre Page');
      let sessionDataServer: any = null; // Use null to allow proper empty session check — {} is truthy
      const preHeaderWithoutDtt: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-request-id': req.headers['x-request-id'] || '',
        Cookie: `sessionID=${req?.cookies?.sessionID};UserIdToken=${req?.cookies?.UserIdToken ? req?.cookies?.UserIdToken : ''
          };MI_SITE=${req?.cookies?.MI_SITE}`,
        'x-host': req.headers['x-host'] || '',
        'accept-language': req.headers['accept-language'] || DEFAULT_LANGUAGE,
      };

      const sessionBody = { keys: NEXT_PUBLIC_SESSION_KEYS }; //body to be send in session call
      // Integrate to pass this flag usePersistentCartInd  via feature flag in future when its ready
      const preBody: { locale: string; usePersistentCartInd?: boolean; isRememberedPC?: boolean } = { locale: '' }; //body to be send in preprocessor call
      let cookieArr: string[] = [];
      let isRLMNonERS = false;
      let enableTargetForRlmNonERS = false;
      let crsType = false;
      let remoteSPANonBulgariLocalesConfig: RemoteSpaLocalesConfig = {};
      let remoteSPABulgariLocalesConfig: RemoteSpaLocalesConfig = {};
      let uxlMigrationStatus = '';
      let sessionUXLMigration = '';
      let consumerId = '';
      let isRememberedUser = false;
      let isAuthenticated = false;
      const params = new URLSearchParams(queryParams);
      // Allow internal Rokt SDK testing on Book reservation routes through
      // either the `rokt=true` query parameter or request header.
      const enableRokt = req.headers?.rokt === 'true' || params.get('rokt') === 'true';

      if (isServerSession(pageUrl, req)) {
        //making server side session call
        const { sessionData, sessionHeader } = await getSessionData(preHeaderWithoutDtt, sessionBody);
        sessionDataServer = sessionData;
        const { isErs, crsSystem, isACRS, sessionUXLMigrationStatus, consumerID, userState, isAuth } =
          transformResponse(sessionDataServer, {
            'cacheData.data.AriesReservation.ersProperty': 'isErs',
            'cacheData.data.AriesReservation.crsSystem': 'crsSystem',
            'cacheData.data.AriesReservation.isACRS': 'isACRS',
            'cacheData.data.AriesReservation.uxlMigrationStatus': 'sessionUXLMigrationStatus',
            'cacheData.data.consumerID': 'consumerID',
            'cacheData.data.AriesCommon.memState': 'userState',
            authenticated: 'isAuth',
          });
        isRememberedUser =
          userState?.toLowerCase() === REMEMBERED_STATE ||
          (!isAuth && req?.cookies?.['RememberMeFlag']?.toLowerCase() === 'true');
        consumerId = consumerID ?? '';
        isRLMNonERS = !isErs && pageUrl.includes(URL_CONSTANTS.RATE_LIST_MENU_URL);
        crsType = isACRS === true || crsSystem === ACRS_CONSTANTS?.ACRS;
        cookieArr = sessionHeader['set-cookie'] || [];
        res.setHeader('Set-Cookie', cookieArr);
        sessionUXLMigration = sessionUXLMigrationStatus;
        if (params.has('fromEmail') && params.get('fromEmail') && pageUrl.includes(URL_CONSTANTS.UPGRADE_RES_URL)) {
          uxlMigrationStatus = sessionUXLMigrationStatus;
        }
        isAuthenticated = isAuth;
      }

      let isDTT = false;
      let isACRS = crsType;
      let isNRP = false;
      let isDTTBookNow = true;
      let featureFlags = {};
      let propertyId = null;
      let socialProofingReportEnabled = false;
      let enableNewRLM = true;
      let crsInventoryName = false;
      let enableAcrsFilters = false;
      let NGSFFeatureFlag = false;
      let redemptionFeatureFlag = false;
      let enablePersistentCart = false;
      let enableNewCheckout = false;
      let hideAdvancePurchaseConsent = false;
      let enableNewLegalConsent = false;
      let headerEnabledFeaturesFlags = {};
      let enableMixedInventory = false;
      let enableSecureFieldsR0 = false;
      let acrsIncludeAccRoomsConfig = {};
      let isDttGateOpen = false;

      if (params.has('propertyId')) {
        propertyId = params.get('propertyId');
      }

      if (
        pageUrl.includes(URL_CONSTANTS.UPCOMING_RES_URL) ||
        pageUrl.includes(URL_CONSTANTS.UPCOMING_RES_CANCELLATION) ||
        pageUrl.includes(URL_CONSTANTS.UPCOMING_RES_IFRAME)
      ) {
        if (params.has('propertyId')) {
          propertyId = params.get('propertyId');
          uxlMigrationStatus = Buffer.from(
            JSON.stringify({
              propertyId: propertyId,
            })
          ).toString('base64');
        }
        if (uxlMigrationStatus) {
          sessionUXLMigration = uxlMigrationStatus;
        } else {
          uxlMigrationStatus = sessionUXLMigration;
        }
      }

      if (params.has('bauBook')) {
        const bauBook = params.get('bauBook');
        isDTTBookNow = bauBook === 'false' ? true : bauBook === 'true' ? false : isDTTBookNow;
      } else if (req.headers && req.headers.bauBook) {
        const bauBookHeader = req.headers.bauBook.toString().toLowerCase();
        isDTTBookNow = bauBookHeader === 'false' ? true : bauBookHeader === 'true' ? false : isDTTBookNow;
      }

      if (params.has('dtt')) {
        const dttParam = params.get('dtt');
        isDTT = dttParam === 'true' ? true : dttParam === 'false' ? false : isDTT;
      } else if (req.headers && req.headers.dtt) {
        const dttHeader = req.headers.dtt.toString().toLowerCase();
        isDTT = dttHeader === 'true' ? true : dttHeader === 'false' ? false : isDTT;
      }

      if (params.has('nrp')) {
        isNRP = params.get('nrp') === 'true';
      } else if (req.headers && req.headers.nrp) {
        const nrpHeader = req.headers.nrp.toString().toLowerCase();
        isNRP = nrpHeader === 'true' ? true : nrpHeader === 'false' ? false : isNRP;
      }

      if (stringToBoolean(NEXT_PUBLIC_ENABLE_HARNESS)) {
        try {
          propertyId = propertyId ?? (sessionDataServer as any)?.cacheData?.data?.AriesReservation?.propertyId;
          featureFlags = (await featureFlagService?.getAllFlags({ target })) || {};

          const dttGateHeaderValue = (req.headers as unknown as Record<string, string | string[] | undefined>)[
            DTT_GATE_FLAG_KEY
          ];

          // const dttSecretKey = (featureFlags as Record<string, unknown>)[DTT_GATE_FLAG_KEY]; // Will uncomment once Harness Flag is ready

          // DTT_FEATURE_GATE_HEADER_PASS_VALUE be replaced with Harness Secret key once ready in Harness, hardcoding it for testing purpose
          isDttGateOpen =
            typeof dttGateHeaderValue === 'string' &&
            dttGateHeaderValue?.length > 0 &&
            dttGateHeaderValue === DTT_FEATURE_GATE_HEADER_PASS_VALUE;

          const {
            experience_mcom_book_dtt_properties = {},
            experience_mcom_book_dtt_book_nrp = false,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            experience_mcom_dtt_book_now = true,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            experience_mcom_book_dtt_master_flag = false,
            experience_mcom_book_bau_rlm,
            experience_mcom_book_bau_remotespa_locales,
            experience_mcom_book_bau_bulgari_flow_remotespa_locales,
            experience_mcom_reporting_scheduler_enabled = false,
            experience_mcom_book_dtt_crs_inventory_name = false,
            experience_mcom_book_dtt_enable_acrs_filters = false,
            experience_mcom_book_dtt_use_properties_from_env = false,
            experience_mcom_book_ngsf = false,
            experience_mcom_redemption_notification_feature_flag = false,
            experience_mcom_book_dtt_enable_persistent_cart = false,
            experience_mcom_book_advancepurchase_hide_consent = false,
            experience_mcom_book_dtt_enable_new_legal_consent = false,
            experience_mcom_book_dtt_secure_fields_enabled = false,
            experience_mcom_book_dtt_acrs_show_acc = {},
          } = featureFlags as { [key in FEATURE_FLAGS]?: unknown };
          log.info('Received harness property list: ', experience_mcom_book_dtt_properties);
          log.info('Received harness book dtt master flag: ', experience_mcom_book_dtt_master_flag);
          const { enableTargetForRlmNonERS: _enableTargetForRlmNonERS = false } = experience_mcom_book_bau_rlm as {
            enableTargetForRlmNonERS: boolean;
          };
          enableTargetForRlmNonERS = _enableTargetForRlmNonERS;
          socialProofingReportEnabled = experience_mcom_reporting_scheduler_enabled as boolean;
          redemptionFeatureFlag = experience_mcom_redemption_notification_feature_flag as boolean;
          NGSFFeatureFlag = experience_mcom_book_ngsf as boolean;
          enableNewLegalConsent = experience_mcom_book_dtt_enable_new_legal_consent as boolean;
          enableSecureFieldsR0 = experience_mcom_book_dtt_secure_fields_enabled as boolean;
          const isFeatureRequestedMemo = createIsFeatureRequested(req);

          headerEnabledFeaturesFlags = getDttRequestedFeatureHeaders(
            featureFlags as Record<string, unknown>,
            isFeatureRequestedMemo,
            isDttGateOpen
          );

          if (
            !isACRS ||
            ((req.headers as unknown as Record<string, string | string[] | undefined>)[DISABLE_MI_FOR_RLM] &&
              pageUrl.includes(URL_CONSTANTS.RATE_LIST_MENU_URL))
          ) {
            (headerEnabledFeaturesFlags as Record<string, boolean>)[FEATURE_FLAG_KEYS.ENABLE_MIXED_INVENTORY] = false;
          }

          enableMixedInventory =
            (headerEnabledFeaturesFlags as Record<string, boolean>)[FEATURE_FLAG_KEYS.ENABLE_MIXED_INVENTORY] ??
            enableMixedInventory;

          enableNewRLM =
            (headerEnabledFeaturesFlags as Record<string, boolean>)[FEATURE_FLAG_KEYS.ENABLE_NEW_RLM] ?? enableNewRLM;

          crsInventoryName = isFeatureRequestedMemo(CRS_INVENTORY_NAME, experience_mcom_book_dtt_crs_inventory_name);
          enableAcrsFilters = isFeatureRequestedMemo(ACRS_FILTERS, experience_mcom_book_dtt_enable_acrs_filters);
          enablePersistentCart = isFeatureRequestedMemo(
            PERSISTENT_CART,
            experience_mcom_book_dtt_enable_persistent_cart
          );
          enableNewLegalConsent = isFeatureRequestedMemo(
            ENABLE_NEW_LEGAL_CONSENT,
            experience_mcom_book_dtt_enable_new_legal_consent
          );
          hideAdvancePurchaseConsent = experience_mcom_book_advancepurchase_hide_consent as boolean;
          acrsIncludeAccRoomsConfig = experience_mcom_book_dtt_acrs_show_acc as Record<string, unknown>;
          remoteSPANonBulgariLocalesConfig =
            (experience_mcom_book_bau_remotespa_locales as RemoteSpaLocalesConfig) ?? {};
          remoteSPABulgariLocalesConfig =
            (experience_mcom_book_bau_bulgari_flow_remotespa_locales as RemoteSpaLocalesConfig) ?? {};

          const params = new URLSearchParams(queryParams);

          // Only override isDTTBookNow if it hasn't been set by the parameters
          if (!params.has('bauBook') && req.headers.bauBook === undefined) {
            isDTTBookNow = (experience_mcom_dtt_book_now as boolean) ?? true;
          }

          if (experience_mcom_book_dtt_master_flag === true) {
            isDTT = true;
            if (params?.get('dtt') === 'false' || req?.headers?.dtt === 'false') {
              isDTT = false; // URL Pram and Mod headers takes precedence.
            }
          } else {
            // Always check for `acrs` value
            const dtt = isDttProperty(
              propertyId,
              experience_mcom_book_dtt_properties as HarnessConfigMap,
              experience_mcom_book_dtt_use_properties_from_env
                ? NEXT_PUBLIC_DTT_PROPERTIES.split(',').map(p => p.trim())
                : []
            );

            // If `isACRS` is true, override `isDTT` to true
            if (isACRS) {
              isDTT = true;
            } else {
              isDTT = dtt !== null ? dtt : isDTT;
            }
          }
          if (isDTT && !params.has('nrp') && req.headers.nrp === undefined) {
            isNRP = experience_mcom_book_dtt_book_nrp as boolean;
          }
        } catch (e) {
          log.error(`Feature Flag Service Failed with error: ${e}`);
        }
      } else {
        if (!params.has('dtt') && req.headers.dtt === undefined) {
          isDTT = NEXT_PUBLIC_IS_DTT !== undefined ? stringToBoolean(NEXT_PUBLIC_IS_DTT) : false;
        }
        if (!params.has('nrp') && req.headers.nrp === undefined) {
          isNRP = NEXT_PUBLIC_IS_NRP !== undefined ? stringToBoolean(NEXT_PUBLIC_IS_NRP) : false;
        }
        isACRS = NEXT_PUBLIC_IS_ACRS !== undefined ? stringToBoolean(NEXT_PUBLIC_IS_ACRS) : false;
      }

      // Check header for book-hide-prepay-consent (default: false, request header takes precedence over harness FF)
      if (req.headers && req.headers['book-hide-prepay-consent']) {
        const hidePrepayConsentHeader = req.headers['book-hide-prepay-consent'].toString().toLowerCase();
        hideAdvancePurchaseConsent = hidePrepayConsentHeader === 'true' ? true : false;
      }

      const preProcessorUrl = getPreProcessorUrl(pageUrl, queryParams);
      //CNWEB-3021 exacting the IS_CN_ENV, UXL_IMAGE_CACHE_DOMAIN, BAIDU_MAP_API_KEY  variable from process.env
      const { IS_CN_ENV, UXL_IMAGE_CACHE_DOMAIN, BAIDU_MAP_API_KEY } = process.env;
      const isDev = process.env['NODE_ENV'] === ENV_DEV;
      let cnEnvVars: { [key: string]: string | undefined } = {};
      // CNWEB-3021 Injecting the Image domain for UXL Image URLs through a env variable, in CN env only (behind IS_CN_ENV) check
      // CNWEB-5501 Injecting the baidu map key through a env variable, in CN env only (behind IS_CN_ENV) check
      if (IS_CN_ENV) {
        const { log } = global.loggerInstance('preProcessorCallMocked');
        cnEnvVars = {
          UXL_IMAGE_CACHE_DOMAIN,
          BAIDU_MAP_API_KEY,
        };
        log.debug('cnEnvVars ', cnEnvVars);
      }

      const isFrame =
        contextUrl.includes('creditcardforreviewdetails') ||
        contextUrl.includes('creditcardforgi') ||
        contextUrl.includes('creditcardforrrdgi');
      const resLookupOverlayIframe = contextUrl.includes('resLookupOverlay');

      // Intercept the axios wrapper for the with pre headers
      axiosWrapper.intercept({
        [constants.ACCEPT_LANG]: req.headers['accept-language'] || '',
      });

      log.debug(`req header cookies bau: ${isDTT}`);

      // Feature Flag check for brand 4 proeprties based on env variable and redirect to search unsuccessful page URL
      if (NEXT_PUBLIC_MAXBRAND_DISABLE_FLAG === 'true') {
        if ((sessionDataServer as any)?.cacheData?.data?.AriesReservation?.isMax) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const propertyId = (sessionDataServer as any)?.cacheData?.data?.AriesReservation?.propertyId;
          return {
            redirect: {
              permanent: false,
              destination: `${URL_CONSTANTS.UNAVAILABILITY_URL}${propertyId}`,
            },
          };
        }
      }

      req.headers['dtt'] = isDTT.toString();

      //Interceptor Call
      const interceptorResponse = await interceptor(req, pageUrl);
      if (interceptorResponse?.status !== 200) {
        return redirectTo(res, redirect.redirect.destination, 307);
      }
      const { brandCode: brandCodeCookie, locale, languageTag, crsSystem } = getPreProcessorFromCookie(req);
      log.debug(`:::brandCodeCookie and locale and languageTag::: ${brandCodeCookie} ${locale} ${languageTag}`);

      const apiStartTime = new Date().getTime();
      log.debug('preData & datalayer started');

      const emptyDataObj = {
        data: [{}],
        mvpOffersData: {},
      };

      let predataWithDatalayer;
      const pagesAllowingUxlMigration = [
        URL_CONSTANTS.RRD_URL,
        URL_CONSTANTS.RRD_GI_URL,
        URL_CONSTANTS.UPCOMING_RES_URL,
        URL_CONSTANTS.UPCOMING_RES_CANCELLATION,
        URL_CONSTANTS.UPCOMING_RES_IFRAME,
        URL_CONSTANTS.IFRAME_CARD_URL,
        URL_CONSTANTS.IFRAME_GI_CARD_URL,
      ];
      if (consumerId && enablePersistentCart) {
        preBody.usePersistentCartInd = true;
        if (!isAuthenticated) {
          preBody.isRememberedPC = true;
        }
      }
      log.debug(`:::isCartPersist::: ${params.get('isCartPersist')}`);
      const preHeader = {
        ...preHeaderWithoutDtt,
        dtt: isDTT.toString(),
        ...(pagesAllowingUxlMigration.some(page => pageUrl.includes(page)) && sessionUXLMigration
          ? { 'uxl-migration-status': sessionUXLMigration }
          : {}),
        isCartPersist: params.get('isCartPersist') || 'false',
      };
      const ACDL_AB_TEST_HEADER = Object.entries(req.headers).find(([key,]) => key.trim().toLowerCase() === 'enable_acdl')?.[0];

      let MOD_HEADER_FOR_ACDL_ENABLEMENT;
      if (ACDL_AB_TEST_HEADER) {
        MOD_HEADER_FOR_ACDL_ENABLEMENT = (req.headers as unknown as Record<string, string>)?.[ACDL_AB_TEST_HEADER].toString().toLowerCase() === 'true';
      }    

      const ENABLE_ACDL =
        (featureFlags as { [key in FEATURE_FLAGS]?: unknown })?.['experience_mcom_rtp_enable_acdl'] ?? false;
      if (!isServerPreprocessor(brandCodeCookie, locale)) {
        predataWithDatalayer = await Promise.all([
          preProcessorCallMocked(brandCodeCookie, locale, languageTag, crsSystem),
          // eslint-disable-next-line no-constant-condition
          DATALAYERTYPE === 'traditional' && !ENABLE_ACDL ? getDataLayer(req, sessionDataServer, isDTT) : emptyDataObj,
          getSocialProofingData(
            sessionDataServer,
            pageUrl.includes(URL_CONSTANTS.RATE_LIST_MENU_URL),
            socialProofingReportEnabled
          ),
        ]);
      } else {
        predataWithDatalayer = await Promise.all([
          preProcessorCall(preProcessorUrl || '', preHeader, preBody, isDev),
          // eslint-disable-next-line no-constant-condition
          DATALAYERTYPE === 'traditional' && !ENABLE_ACDL ? getDataLayer(req, sessionDataServer, isDTT) : emptyDataObj,
          getSocialProofingData(
            sessionDataServer,
            pageUrl.includes(URL_CONSTANTS.RATE_LIST_MENU_URL),
            socialProofingReportEnabled
          ),
        ]);
      }

      const [preData, dataLayer, socialProofingData] = predataWithDatalayer;
      dataLayer.data[0] = {
        ...dataLayer.data[0],
        ...(Object.keys(socialProofingData).length > 0 && { prop_social_proofing_data: socialProofingData }),
        // prop_social_Proofing_Data: socialProofingData,
      };

      pLog.log('preData & datalayer success', apiStartTime, new Date().getTime());

      // TODO: Remove header after UAT testing
      log.debug(`req header res-is-sst-enabled: ${req?.headers?.['res-is-sst-enabled']}`);
      log.debug(`req sessionDataServer: ${JSON.stringify(sessionDataServer)}`);

      const sessionLocale = (sessionDataServer as any)?.locale;
      const isbulgariFlow = isBulgariFlowRequest(req, isFrame);
      // Each domain is controlled by its own flag so enabling remote SPA on one domain
      // (non-Bulgari) never unintentionally enables it on another (Bulgari). Bulgari requests match
      // only the Bulgari locale list; non-Bulgari requests match only the non-Bulgari locale list.
      const remoteSPALocales = getRemoteSpaLocalesForDomain(
        remoteSPANonBulgariLocalesConfig,
        remoteSPABulgariLocalesConfig,
        isbulgariFlow
      );

      const isRemoteSPA = remoteSPALocales.includes(sessionLocale) || isRemoteSpaEnabled(req, queryParams);

      log.debug(`flag-isRemoteSPA: ${isRemoteSPA}`);
      log.debug(`flag-isDTT: ${isDTT}`);
      const isExpLocaleMatch = (NEXT_PUBLIC_NEWGUESTINFO_EXP_LOCALES?.split(',') ?? []).some(
        l => l.trim() === (sessionDataServer as any)?.locale
      );

      const isSpecialRate =
        sessionDataServer?.cacheData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.clusterCode?.toUpperCase() !==
        'NONE' ||
        sessionDataServer?.cacheData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.rewardsRedemption;

      const serverExpFragArr: string[] = [];
      const targetContentArr: string[] = [];
      const isRRDGI = pageUrl.includes(URL_CONSTANTS.RRD_GI_URL);
      const isRRD = pageUrl.includes(URL_CONSTANTS.RRD_URL);
      // use the R0 design if the page's RRD or RRDGI
      const isR0Page = isRRDGI || isRRD;
      const newCheckoutDesignExpFragment = isRRDGI ? R0_CHECKOUT_GUEST_EXP_SEGMENT : R0_CHECKOUT_MEMBER_EXP_SEGMENT;
      const r0CheckoutHeader = req.headers['r0-checkout'] ? true : false;
      //Enable new checkout design for RRD/RRDGI pages when the r0-checkout header is present.
      enableNewCheckout = (r0CheckoutHeader || enableMixedInventory) && isR0Page;
      let securePaymentTargetFlag = false;
      let isSST: boolean | undefined = false;
      const targetMessages: Record<string, unknown> = {};
      const acdlEnabledPagesByLocales =
        (featureFlags as { [key in FEATURE_FLAGS]?: unknown })?.[
          'experience_mcom_book_acdl_enabled_pages_by_locales'
        ] ?? '';
      // Feature flag to disable AEP call even if ACDL is enabled
      const DISABLE_ACDL_AEP_CALL = 
        (featureFlags as { [key in FEATURE_FLAGS]?: unknown })?.[
          'experience_mcom_book_disable_acdl_aep_call'
        ] ?? '';

      log.debug(`MOD_HEADER_FOR_ACDL_ENABLEMENT: ${MOD_HEADER_FOR_ACDL_ENABLEMENT}`);
      log.debug(`DISABLE_ACDL_AEP_CALL: ${DISABLE_ACDL_AEP_CALL}`);

      /**
       * Resolves whether the current page/locale is ACDL-enabled for server-side personalization.
       * Prefers the mod-header override when present; otherwise checks env + Harness locale map.
       * Bootstrap AEM model may not yet reflect the final enableWebSDK (experience fragments are applied later),
       * so the model-level enableWebSDK check is deferred to the final AEM model used by the page.
       */
      const resolveIsPageEnabledWithAcdl = (acdlModel: Record<string, unknown>): boolean =>
        MOD_HEADER_FOR_ACDL_ENABLEMENT ??
        isACDLEnabled(acdlModel, acdlEnabledPagesByLocales, pageUrl);

      // When true, server-side personalization uses AEP Edge Interact; otherwise SST delivery is used.
      const isPageEnabledWithAcdl = resolveIsPageEnabledWithAcdl({
        language: preData?.languageTag || preData?.locale || languageTag || '',
        enableWebSDK: 'true', //For server side personalization, dont need to check for this aem flag, thats why passing true here
      });

      targetMessages['isPageEnabledWithAcdl'] = isPageEnabledWithAcdl;

      /**
       * Processes normalized mbox results from either server-side personalization path:
       * - AEP Edge Interact (`handle[].payload[]` mapped to mbox/options)
       * - SST delivery (`data.execute.mboxes`)
       * Sets flags, tracking content, and PHX_BOOK_SST_TRACKING cookie.
       */
      const processServerPersonalizationMboxResponse = (mboxes: { name: string; options?: any[] }[]) => {
        let targetTokens = {};

        mboxes.forEach((mBox: { name: string; options: any[] }) => {
          if (mBox?.options?.length) {
            const mboxName = mBox.name;
            mBox?.options?.forEach(option => {
              if (option?.content) {
                if (mboxName === SERVER_TARGET_MBOX_SECURE_FIELDS && option?.content === TRUE) {
                  securePaymentTargetFlag = true;
                }
                targetContentArr.push(option?.content);
                if (option?.responseTokens) {
                  targetTokens = {
                    ...targetTokens,
                    'activity.name': option.responseTokens['activity.name'],
                    'option.name': option.responseTokens['option.name'],
                  };
                }
                log.debug(`Evaluating mbox: ${mboxName} with content: ${option?.content} for new checkout design`);
                const isR0VariantMbox =
                  mBox.name?.includes(SERVER_TARGET_R0_CHECKOUT_NAME) && option?.content === 'R0Variant';
                if (isR0VariantMbox && isR0Page) {
                  enableNewCheckout = true;
                }
              } else {
                cookieArr.push(`${TARGET_COOKIE_NAME}=;`);
              }
            });
          } else {
            cookieArr.push(`${TARGET_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`);
          }
        });

        const enhancedCookieData = {
          content: targetContentArr,
          hasContent: targetContentArr.length > 0,
          tokens: targetTokens,
        };

        cookieArr.push(`${TARGET_COOKIE_NAME}= ${JSON.stringify(enhancedCookieData)};`);
      };

      /**
       * ACDL path: calls AEP Edge Interact, maps personalization:decisions payload to mboxes,
       * then runs shared mbox/cookie processing via processServerPersonalizationMboxResponse.
       */
      const fetchAepData = async () => {
        const targetPayload = targetPayloadGenerator(
          dataLayer,
          sessionDataServer,
          pageUrl,
          req.headers,
          queryParams
        )(createDefaultPayload());
        const decisionScopes = targetPayload.execute?.mboxes?.map((mbox: { name: string }) => mbox.name) ?? [];
        const targetParameters = targetPayload.execute?.mboxes?.[0]?.parameters ?? {};
        const fullPageUrl = targetPayload.context?.address?.url ?? pageUrl;

        const serverTargetResponse = await getAdobeAEPData(
          fullPageUrl,
          decisionScopes,
          targetParameters,
          req.cookies as unknown as Record<string, string>
        );

        targetMessages['adobe_TargetDataResponse'] = serverTargetResponse?.eventInfo;

        // Debug curl for the AEP Edge interact request (built in getAdobeAEPData as messages.adobe_interact_curl).
        const aepInteractCurl = serverTargetResponse?.messages?.['adobe_interact_curl'];
        if (aepInteractCurl) {
          log.debug(`AEP Edge interact curl: ${aepInteractCurl}`);
        }

        // Raw AEP Edge interact response from messages.adobe_interact_response (typed locally for handle access).
        const adobeInteractResponse = serverTargetResponse?.messages?.adobe_interact_response as {
          handle?: Array<{
            type: string;
            payload?: Array<{
              scope: string;
              items?: Array<{ data?: { content?: string }; meta?: Record<string, string> }>;
            }>;
          }>;
        };

        // personalization:decisions handle contains proposition payloads for each requested decision scope.
        const aepDecisionsHandle = adobeInteractResponse?.handle?.find(
          item => item.type === 'personalization:decisions'
        );

        // Map Edge proposition shape to SST-compatible mboxes: scope -> name, items[].data.content -> options[].content.
        const aepMboxes =
          aepDecisionsHandle?.payload?.map(proposition => ({
            name: proposition.scope,
            options:
              proposition.items?.map(item => ({
                content: item?.data?.content,
                responseTokens: item?.meta,
              })) ?? [],
          })) ?? [];

        if (aepMboxes?.length) {
          processServerPersonalizationMboxResponse(aepMboxes);
        } else {
          cookieArr.push(`${TARGET_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`);
        }
      };

      /**
       * Legacy SST path: Target delivery call (data.execute.mboxes),
       * then runs shared mbox/cookie processing via processServerPersonalizationMboxResponse.
       */
      const fetchSstTargetData = async () => {
        const serverTargetResponse = await getTargetExpSegment(
          process.env['NEXT_PUBLIC_SERVER_TARGET_URL'],
          undefined,
          targetPayloadGenerator(dataLayer, sessionDataServer, pageUrl, req.headers, queryParams),
          {
            client: SERVER_TARGET_MBOX_CLIENT,
            version: SERVER_TARGET_MBOX_VERSION,
            sessionId: (sessionDataServer as any)?.sessionToken,
          }
        );
        log.debug(`target response: ${JSON.stringify(serverTargetResponse)}`);
        processServerPersonalizationMboxResponse(serverTargetResponse?.data?.execute?.mboxes ?? []);
      };

      /**
       * Shared post-mbox experience fragment logic for both server-side personalization paths (AEP Edge Interact and SST delivery).
       * Pushes AEM segments into serverExpFragArr using page context and flags set by processServerPersonalizationMboxResponse (e.g. enableNewCheckout).
       */
      const applyServerExperienceFragments = () => {
        const isRLM = pageUrl.includes(URL_CONSTANTS.RATE_LIST_MENU_URL);
        if (isRLM && DEFAULT_LOCALE === preData.locale) {
          serverExpFragArr.push(NEW_RLM_REDESIGN);
        }

        if (!isbulgariFlow && enableNewCheckout) {
          const hybridPageTargetExp = getHybridPageTargetExperience(
            pageUrl,
            sessionDataServer as IGetHybridPageTargetExperienceArgs,
            enableNewCheckout
          );
          log.debug(`hybridPageTargetExp : ${hybridPageTargetExp}`);
          if (hybridPageTargetExp && isExpLocaleMatch) {
            serverExpFragArr.push(hybridPageTargetExp);
          } else {
            serverExpFragArr.push(newCheckoutDesignExpFragment);
            log.debug(`newCheckoutDesignExpFragment : ${newCheckoutDesignExpFragment}`);
          }
        }

        log.debug(`recipe array: ${serverExpFragArr}`);
        log.debug(`target recipe array: ${targetContentArr}`);
      };

      /**
       * Applies default AEM experience segments when mbox processing did not populate serverExpFragArr.
       * Shared by AEP Edge Interact and SST delivery; for eligible locales. 
       * Selects hybrid checkout or R0 checkout member/guest fragments based on page and enableNewCheckout.
       */
      const applyDefaultServerExperienceFragments = () => {
        const shouldTriggerExpFlow = serverExpFragArr.length === 0 && isExpLocaleMatch;

        if (!isbulgariFlow && shouldTriggerExpFlow) {
          const hybridPageTargetExp = getHybridPageTargetExperience(
            pageUrl,
            sessionDataServer as IGetHybridPageTargetExperienceArgs,
            enableNewCheckout
          );
          log.debug(`hybridPageTargetExp : ${hybridPageTargetExp}`);
          if (hybridPageTargetExp) {
            serverExpFragArr.push(hybridPageTargetExp);
          }
          if (!hybridPageTargetExp && isR0Page && enableNewCheckout) {
            log.debug(`R0 CHECKOUT MEMBER PAGE : ${newCheckoutDesignExpFragment}`);
            serverExpFragArr.push(newCheckoutDesignExpFragment);
          }
        }
      };

      /** 
       * ACDL path: AEP Edge target call.
       * Legacy path: server-side SST target call.
       */
      if (!isFrame) {
        isSST = isSSTEnabled(
          pageUrl,
          req.headers['res-is-sst-enabled'],
          () => !isRLMNonERS || (enableTargetForRlmNonERS && (!isACRS || enableNewRLM)) || isR0Page
        );
        log.debug(`isSSTEnabled: ${isSST}`);

        if (isSST) {
          try {
            if (isPageEnabledWithAcdl && !DISABLE_ACDL_AEP_CALL) {
              await fetchAepData();
            } else {
              await fetchSstTargetData();
            }

            applyServerExperienceFragments();
          } catch (e) {
            log.error(`error occurred: ${e}`);
          }
        } else {
          cookieArr.push(`${TARGET_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`);
        }

        log.debug(`res-is-sst-enabled : ${req.headers['res-is-sst-enabled']}`);
        applyDefaultServerExperienceFragments();
      }

       if (!isFrame) {
        if (isbulgariFlow) {
          cookieArr.push(`${'isBulgari'}=true;`);
        } else {
          cookieArr.push(`${'isBulgari'}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`);
        }
      }
      //is Feature Flag enableTargetForRlmNonERS is true and flow is RLM Non ERS with Non Special rate then load RLM 2.0
      if (
        !isbulgariFlow &&
        (!isACRS || enableNewRLM) &&
        (enableTargetForRlmNonERS ? !isSpecialRate : isRLMNonERS) &&
        isRLMNonERS &&
        DEFAULT_LOCALE === preData.locale
      ) {
        serverExpFragArr.splice(0, serverExpFragArr.length);
        serverExpFragArr.push('newRLMRedesign');
      }

      // TODO: Temporary code to run pwdless enrollment and RLM 2.0 page through mod header or query param.
      //  pwdless enrollment - newGuestInfo
      //  RLM 2.0 page - newRLMRedesign
      if (serverExpFragArr.length < 1) {
        const value = getHeaderValue(req, pageUrl, queryParams);
        value && serverExpFragArr.push(value);
      }

      const NGSFHeaderFlag = req.headers['show-ngsf'];
      const isNGSFFlag = getNGSFFlag(NGSFHeaderFlag, NGSFFeatureFlag, pageUrl, sessionDataServer);

      /**
       * Secure fields behavior for the new checkout flow:
       * - When the DTT gate is closed, ignore the override header and force it to FALSE.
       * - When both `enableNewCheckout` and `isDttGateOpen` are true, prefer header-only control
       *   by forcing `effectiveSecurePaymentFlag` to false.
       * - For R0 checkout, enable secure payment only when both the target flag
       *   and the R0 secure fields feature flag are enabled.
       * - For non-R0 checkout, rely only on the secure payment target flag.
       */
      const secureFieldsHeaderFlag =
        enableNewCheckout && !isDttGateOpen
          ? constants.FALSE
          : (req.headers as unknown as Record<string, string | string[] | undefined>)[SECURE_FIELDS_ENABLED];
      const securePaymentFlag = enableNewCheckout
        ? securePaymentTargetFlag && enableSecureFieldsR0
        : securePaymentTargetFlag;
      const effectiveSecurePaymentFlag = enableNewCheckout && isDttGateOpen ? false : securePaymentFlag;
      const isSecureFieldsPayment = getSecureFieldFlag(secureFieldsHeaderFlag, isDTT, effectiveSecurePaymentFlag);

      //New method getDynamicPageModel for calling dynamic routing page model response
      let model = await getDynamicPageModel(
        isRemoteSPA,
        pageUrl,
        preHeader,
        preData,
        sessionDataServer || { sessionToken: req.cookies?.sessionID },
        serverExpFragArr,
        isbulgariFlow
      );

      model = replaceKeyInJson(model, 'appliedCssClassNames', 'styleclass');

      const { subDirectoryContextPath = '' } = model || {};

      if (!isFrame && isSST) {
        log.debug(`isRememberedUser: ${isRememberedUser}, isRRD: ${isRRD}, enableNewCheckout: ${enableNewCheckout}`);
        if (enableNewCheckout && isRememberedUser && isRRD) {
          log.debug(`Redirecting to hybrid page URL`);
          return redirectTo(res, addSubDirectoryPrefix(URL_CONSTANTS.HYBRID_PAGE_URL, subDirectoryContextPath), 307);
        }
      }
      const { location, resCode } = evaluatePreData(preData, subDirectoryContextPath, pageUrl);

      if (location) {
        return redirectTo(res, location, resCode);
      }

      const apolloEnvVars = {
        APOLLOGRAPHQL_PUBLIC_ACCEPT_LANG,
        APOLLOGRAPHQL_PUBLIC_CLIENT_VERSION,
        APOLLOGRAPHQL_PUBLIC_CLIENT_NAME,
        APOLLOGRAPHQL_PUBLIC_APPLICATION_NAME,
        APOLLOGRAPHQL_PUBLIC_REQUIRE_SAFELISTING,
        APOLLOGRAPHQL_FULL_NGINX_ENDPOINT,
        APOLLOGRAPHQL_PUBLIC_LOCAL_DEV,
        APOLLOGRAPHQL_PUBLIC_LOWER_ENV_PATH,
        APOLLOGRAPHQL_PUBLIC_HIGHER_ENV_PATH,
        DEPLOYED_ENV_TYPE,
        APOLLOGRAPHQL_PUBLIC_DEBUG_LOG,
        APOLLOGRAPHQL_LOCAL_ENDPOINT,
      };

      if (ACDL_AB_TEST_HEADER) {
        // if we have this mod header added for ab testing, then we should use that value instead of server computed flag
        model = {
          ...model,
          MOD_HEADER_FOR_ACDL_ENABLEMENT,
          enableWebSDK: MOD_HEADER_FOR_ACDL_ENABLEMENT,
          ALL_FLAGS_RELATED_TO_ACDL_MODIFIED: true,
        };
      }

      const gsspData = gssp(preData, model, featureFlags, pageUrl); // Run `getServerSideProps` to get page-specific data
      model = {
        ...model,
        ...(gsspData?.modelProps ?? {}),
      };
      let brandCode = preData?.experienceSegments?.brandCode?.toLowerCase() || MARRIOTT_BRAND_CODE;

      // To read the CRS System in V2 pages like upcoming pages guest and auth user flows.
      if (
        pageUrl.includes(URL_CONSTANTS.UPCOMING_RES_URL) ||
        pageUrl.includes(URL_CONSTANTS.UPCOMING_RES_CANCELLATION)
      ) {
        log.debug(`crsSystem upcoming : ${preData?.experienceSegments?.crsSystem}`);

        isACRS = preData?.experienceSegments?.crsSystem === ACRS_CONSTANTS?.ACRS || isACRS;
      }

      if (resLookupOverlayIframe) brandCode = '';

      const graphqlLocale = preData?.languageTag || ''; //get the graphQL locale from preprocessor call
      const serverEnv: Record<string, string | undefined> = {};
      const parsedServerEnvs = process.env;
      NEXT_PUBLIC_BOOK_KEYS.split('|').forEach(_i => {
        if (parsedServerEnvs[_i]) {
          serverEnv[_i] = parsedServerEnvs[_i];
        }
      });

      // Injecting ACDL related environment variables into serverEnv
      Object.keys(envVarKeys).forEach(key => {
        if (parsedServerEnvs[key]) {
          serverEnv[key] = parsedServerEnvs[key];
        }
      });

      // Injecting page_experience into data layer for ReviewDetails, Review Guest information pages,
      // The page_experience value will be for non R0 "page_experience: Web Checkout Page 1.0" or for R0 "page_experience: Web Checkout Page 2.0".

      if (dataLayer?.data?.[0]) {
        if (isR0Page) {
          (dataLayer?.data[0] as { page_experience?: string })['page_experience'] = enableNewCheckout
            ? DATA_LAYER_PAGE_EXPERIENCE_CO.V2
            : DATA_LAYER_PAGE_EXPERIENCE_CO.V1;
        }
        if (pageUrl.includes(URL_CONSTANTS.UPCOMING_RES_URL) || isR0Page) {
          (dataLayer?.data[0] as { secureFields?: boolean })['secureFields'] = Boolean(isSecureFieldsPayment);
        }
      }

      const isTagpCookie = getTAGPCookie(req);
      if (isTagpCookie) {
        cookieArr.push(`${IS_TAGP_COOKIE}=true;`);
      } else {
        cookieArr.push(`${IS_TAGP_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`);
      }

      // Setting all cookies present in cookieArr
      res.setHeader('Set-Cookie', cookieArr);

      const filteredHeaderData = {
        'x-akamai-edgescape': req.headers['x-akamai-edgescape'],
      };

      return {
        model: model ?? {},
        SSR_LOGS: targetMessages,
        serverSideInteractMessages: {
          adobe_TargetDataResponse: targetMessages['adobe_TargetDataResponse'],
        },
        sessionData: sessionDataServer,
        brandCode,
        graphqlLocale,
        dataLayer,
        isIframeCard: isFrame,
        serverENV: JSON.stringify({ ...serverEnv, ...(isDev && { PROXY_HOST: NEXT_PUBLIC_PROXY_HOST }) }),
        ...gsspData.props,
        cnEnvVars,
        isDTT,
        isACRS,
        isDTTBookNow,
        uxlMigrationStatus,
        propertyId,
        isNRP,
        apolloEnvVars,
        requestId,
        isTagpCookie,
        isbulgariFlow,
        featureFlags,
        isRemoteSPA,
        isSecurePayment: isSecureFieldsPayment,
        crsInventoryName,
        enableAcrsFilters,
        redemptionFeatureFlag,
        enablePersistentCart,
        enableNewLegalConsent,
        enableNewCheckout,
        isNGSFFlag,
        enableRokt,
        hideAdvancePurchaseConsent,
        headerEnabledFeaturesFlags,
        acrsIncludeAccRoomsConfig,
        resolvedUrl: pageUrl,
        headersData: filteredHeaderData,
      };
    } catch (e) {
      const { log } = global.loggerInstance('Pre Page');
      log.error(`error occurred: ${e}`);
      // return redirect;
      return redirectTo(res, redirect.redirect.destination, 307);
    }
  };
}
